import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, X, Mail } from "lucide-react";
import {
  createMeetingRoom,
  checkRoomNameExists,
} from "../../../../services/roomServices";
import { toast } from "react-toastify";
import userService from "../../../../services/userServices";

const AddNewRoom = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const passwordInputRefs = useRef([null, null, null, null]);
  const [showModeratorCountDropdown, setShowModeratorCountDropdown] =
    useState(false);
  const [roomNameChecking, setRoomNameChecking] = useState(false);
  const [roomNameExists, setRoomNameExists] = useState(false);
  const [roomNameMessage, setRoomNameMessage] = useState("");

  // Co-Host state
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Speakers state
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [speakersList, setSpeakersList] = useState([]);
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] =
    useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  // Common email domains for suggestions
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };
  const [formData, setFormData] = useState({
    roomName: "",
    roomType: "Meeting",
    description: "",
    scheduledAt: "",
    duration: 60,
    maxParticipants: 100,
    // Room Settings
    allowChat: true,
    allowScreenSharing: true,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    guestCanUnmute: true,
    requirePassword: false,
    password: "",
    hostApprovalRequired: false,
    // Exit URL
    exitUrlEnabled: false,
    exitUrl: "",
    // Custom Button
    customButtonEnabled: false,
    customButtonText: "",
    customButtonUrl: "",
    // Waiting Room Text
    waitingRoomTextEnabled: false,
    waitingRoomTitle: "",
    waitingRoomMessage: "",
    waitingRoomSubText: "",
    // Custom Background
    customBackgroundEnabled: false,
    backgroundType: "solid",
    backgroundColor: "#1a1a2e",
    gradientColor1: "#1a1a2e",
    gradientColor2: "#615fff",
    gradientDirection: "to right",
    backgroundImage: null,
    backgroundImagePreview: null,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear room name exists error when user starts typing
    if (field === "roomName" && (roomNameExists || roomNameMessage)) {
      setRoomNameExists(false);
      setRoomNameMessage("");
    }
  };

  // Handle password digit input
  const handlePasswordDigitChange = (index, value) => {
    // Only allow numbers
    const digit = value.replace(/\D/g, "");
    if (digit.length > 1) return; // Only allow single digit

    const currentPassword = formData.password.split("");
    currentPassword[index] = digit;
    const newPassword = currentPassword.join("").slice(0, 4);
    
    handleInputChange("password", newPassword);

    // Auto-focus next input if digit entered
    if (digit && index < 3 && passwordInputRefs.current[index + 1]) {
      passwordInputRefs.current[index + 1].focus();
    }
  };

  // Handle password backspace
  const handlePasswordKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formData.password[index] && index > 0) {
      // If current field is empty and backspace is pressed, focus previous field
      passwordInputRefs.current[index - 1].focus();
    }
  };

  // Handle paste for password
  const handlePasswordPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pastedData) {
      handleInputChange("password", pastedData);
      // Focus the next empty field or the last field
      const nextIndex = Math.min(pastedData.length, 3);
      if (passwordInputRefs.current[nextIndex]) {
        passwordInputRefs.current[nextIndex].focus();
      }
    }
  };

  const handleRoomNameBlur = async () => {
    const roomName = formData.roomName.trim();

    // Only check if room name is valid and not empty
    if (!roomName || roomName.length < 3) {
      return;
    }

    setRoomNameChecking(true);
    try {
      const response = await checkRoomNameExists(roomName);
      const exists = response.isexist || false;
      setRoomNameExists(exists);

      if (exists) {
        setRoomNameMessage("Room name already exists");
      } else {
        setRoomNameMessage("Room name is available");
      }
    } catch (error) {
      console.error("Error checking room name:", error);
      // Don't show error toast for API failures, just log it
    } finally {
      setRoomNameChecking(false);
    }
  };

  // Generate suggestions based on input (Co-Host)
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }

    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]);

  // Check if user exists when valid email is entered (Co-Host)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setUserExists(false);
          } else {
            setUserExists(false);
            console.error("Error checking user:", error);
          }
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Generate speaker suggestions based on input
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSpeakerSuggestions(true);
    } else {
      setShowSpeakerSuggestions(false);
    }

    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if speaker user exists when valid email is entered
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setSpeakerUserExists(false);
          } else {
            setSpeakerUserExists(false);
            console.error("Error checking speaker user:", error);
          }
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  // Handle adding co-host (existing user or invite)
  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          console.error("Error checking user:", error);
        }
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  // Handle adding speaker (existing user or invite)
  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          console.error("Error checking speaker user:", error);
        }
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.roomName.trim()) {
      toast.error("Please enter a room name");
      return;
    }

    if (formData.roomName.trim().length < 3) {
      toast.error("Room name must be at least 3 characters long");
      return;
    }

    if (roomNameExists) {
      toast.error("Room name already exists. Please choose a different name.");
      return;
    }

    if (formData.requirePassword) {
      if (!formData.password.trim()) {
        toast.error("Please enter a password for the room");
        return;
      }
      if (formData.password.length !== 4 || !/^\d{4}$/.test(formData.password)) {
        toast.error("Password must be exactly 4 digits");
        return;
      }
    }

    if (formData.exitUrlEnabled) {
      if (!formData.exitUrl.trim()) {
        toast.error("Please enter an exit URL");
        return;
      }

      if (!isValidUrl(formData.exitUrl.trim())) {
        toast.error("Please enter a valid URL (e.g., https://example.com)");
        return;
      }
    }

    if (formData.customButtonEnabled) {
      if (!formData.customButtonText.trim()) {
        toast.error("Please enter a button name");
        return;
      }

      if (!formData.customButtonUrl.trim()) {
        toast.error("Please enter a button URL");
        return;
      }

      if (!isValidUrl(formData.customButtonUrl.trim())) {
        toast.error(
          "Please enter a valid button URL (e.g., https://example.com)"
        );
        return;
      }
    }

    if (formData.waitingRoomTextEnabled) {
      if (!formData.waitingRoomTitle.trim()) {
        toast.error("Please enter waiting room title");
        return;
      }

      if (!formData.waitingRoomMessage.trim()) {
        toast.error("Please enter waiting room message");
        return;
      }

      if (!formData.waitingRoomSubText.trim()) {
        toast.error("Please enter waiting room sub text");
        return;
      }
    }

    if (formData.customBackgroundEnabled) {
      if (
        formData.backgroundType === "image" &&
        !formData.backgroundImage &&
        !formData.backgroundImagePreview
      ) {
        toast.error("Please select a background image");
        return;
      }

      if (formData.backgroundType === "image" && formData.backgroundImage) {
        const maxSize = 5 * 1024 * 1024;
        if (formData.backgroundImage.size > maxSize) {
          toast.error("Background image size should not exceed 5MB");
          return;
        }
      }
    }

    try {
      const submitData = new FormData();

      // Add timezone
      submitData.append(
        "timezone",
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );

      // Add all form data (including roomName and roomType)
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== "") {
          if (key === "backgroundImage" && value instanceof File) {
            submitData.append(key, value);
          } else if (
            typeof value === "boolean" ||
            typeof value === "string" ||
            typeof value === "number"
          ) {
            submitData.append(key, value);
          }
        }
      });

      // Add emails as JSON string
      if (invitesList.length > 0) {
        submitData.append("emails", JSON.stringify(invitesList));
      }

      // Add speakers as JSON string
      if (speakersList.length > 0) {
        submitData.append("speakers", JSON.stringify(speakersList));
      }

      await createMeetingRoom(submitData);
      toast.success("Room created successfully");
      navigate("/dashboard/rooms");
    } catch (err) {
      console.error("Error creating room:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to create room. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/dashboard/rooms")}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <X size={20} className="mr-2" />
            Back to Rooms
          </button>
          <h1 className="text-3xl font-bold text-white">Create New Room</h1>
        </div>

        {/* Create Form */}
        <div className="rounded-xl border border-gray-600 p-8 primary-card-background">
          {/* Room Name */}
          <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Room Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.roomName}
                onChange={(e) => handleInputChange("roomName", e.target.value)}
                onBlur={handleRoomNameBlur}
                placeholder="Enter room name"
                maxLength={25}
                disabled={roomNameChecking}
                className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                  formData.roomName.trim() &&
                  formData.roomName.trim().length < 3
                    ? "border-red-500 focus:ring-red-500"
                    : roomNameExists
                    ? "border-red-500 focus:ring-red-500"
                    : roomNameMessage && !roomNameExists
                    ? "border-green-500 focus:ring-green-500"
                    : "border-gray-600 focus:ring-[#6A1FBF]"
                } ${roomNameChecking ? "opacity-50" : ""}`}
              />
              <p className="text-gray-400 text-xs mt-1">
                {roomNameChecking ? (
                  <span className="text-blue-400">
                    Checking room name availability...
                  </span>
                ) : formData.roomName.trim() &&
                  formData.roomName.trim().length < 3 ? (
                  <span className="text-red-400">
                    Minimum 3 characters required
                  </span>
                ) : roomNameMessage ? (
                  <span
                    className={
                      roomNameExists ? "text-red-400" : "text-green-400"
                    }
                  >
                    {roomNameMessage}
                  </span>
                ) : (
                  "25 characters max"
                )}
              </p>
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Room Type
              </label>
              <select
                value={formData.roomType}
                onChange={(e) => handleInputChange("roomType", e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
              >
                <option value="Meeting">Meeting</option>
                <option value="Webinar">Webinar</option>
              </select>
            </div>
          </div>

          {/* Co-Host */}
          <div className="mb-8">
            <h3 className="text-white text-lg font-semibold mb-4">Co-Host</h3>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={invitesEmailInput}
                    onChange={(e) => setInvitesEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (
                          selectedSuggestionIndex >= 0 &&
                          suggestions[selectedSuggestionIndex]
                        ) {
                          e.preventDefault();
                          const selectedEmail =
                            suggestions[selectedSuggestionIndex];
                          handleAddCoHost(selectedEmail, null);
                          setSelectedSuggestionIndex(-1);
                        } else if (invitesEmailInput.trim() && !checkingUser) {
                          e.preventDefault();
                          const email = invitesEmailInput.trim();
                          handleAddCoHost(email, userExists === false);
                        }
                      } else if (
                        e.key === "ArrowDown" &&
                        suggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSuggestions(true);
                        setSelectedSuggestionIndex((prev) =>
                          prev < suggestions.length - 1 ? prev + 1 : prev
                        );
                      } else if (
                        e.key === "ArrowUp" &&
                        suggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSuggestions(true);
                        setSelectedSuggestionIndex((prev) =>
                          prev > 0 ? prev - 1 : 0
                        );
                      } else if (e.key === "Escape") {
                        setShowSuggestions(false);
                        setSelectedSuggestionIndex(-1);
                      }
                    }}
                    onFocus={() => {
                      if (invitesEmailInput.trim().length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="Add co-host email and press Enter"
                    className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {/* User Status Indicator */}
                  {invitesEmailInput.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingUser ? (
                        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : userExists === true ? (
                        <div className="text-green-500 text-sm">✓</div>
                      ) : userExists === false ? (
                        <Mail className="w-5 h-5 text-orange-500" />
                      ) : null}
                    </div>
                  )}
                  {/* Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            handleAddCoHost(suggestion, null);
                          }}
                          className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                            selectedSuggestionIndex === index
                              ? "bg-indigo-100"
                              : ""
                          }`}
                        >
                          <div className="text-sm text-gray-700">
                            {suggestion}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* User Exists/Invite Options */}
                  {!showSuggestions &&
                    invitesEmailInput.trim() &&
                    userExists !== null &&
                    !checkingUser && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        {userExists ? (
                          <div
                            onClick={() =>
                              handleAddCoHost(invitesEmailInput.trim(), false)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-green-500">✓</div>
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {invitesEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User exists - Add as Co-Host
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() =>
                              handleAddCoHost(invitesEmailInput.trim(), true)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <Mail className="w-5 h-5 text-orange-500" />
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {invitesEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User not found - Send Email Invite
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
                <button
                  onClick={() => {
                    if (invitesEmailInput.trim()) {
                      const email = invitesEmailInput.trim();
                      if (userExists === false) {
                        handleAddCoHost(email, true);
                      } else {
                        handleAddCoHost(email, false);
                      }
                    }
                  }}
                  disabled={checkingUser}
                  className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingUser ? "..." : "Add"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {invitesList.map((email, index) => {
                const isInvite = inviteEmails.includes(email);
                return (
                  <span
                    key={index}
                    className={`flex items-center px-3 py-1 rounded-full text-sm ${
                      isInvite
                        ? "bg-orange-600 text-white"
                        : "bg-gray-700 text-white"
                    }`}
                  >
                    {isInvite && <Mail className="w-3 h-3 mr-1" />}
                    {email}
                    <button
                      onClick={() => {
                        setInvitesList((prev) =>
                          prev.filter((item) => item !== email)
                        );
                        setInviteEmails((prev) =>
                          prev.filter((item) => item !== email)
                        );
                      }}
                      className="ml-2 text-gray-400 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Speakers */}
          <div className="mb-8">
            <h3 className="text-white text-lg font-semibold mb-4">Speakers</h3>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={speakersEmailInput}
                    onChange={(e) => setSpeakersEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (
                          selectedSpeakerSuggestionIndex >= 0 &&
                          speakerSuggestions[selectedSpeakerSuggestionIndex]
                        ) {
                          e.preventDefault();
                          const selectedEmail =
                            speakerSuggestions[selectedSpeakerSuggestionIndex];
                          handleAddSpeaker(selectedEmail, null);
                          setSelectedSpeakerSuggestionIndex(-1);
                        } else if (
                          speakersEmailInput.trim() &&
                          !checkingSpeakerUser
                        ) {
                          e.preventDefault();
                          const email = speakersEmailInput.trim();
                          handleAddSpeaker(email, speakerUserExists === false);
                        }
                      } else if (
                        e.key === "ArrowDown" &&
                        speakerSuggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSpeakerSuggestions(true);
                        setSelectedSpeakerSuggestionIndex((prev) =>
                          prev < speakerSuggestions.length - 1 ? prev + 1 : prev
                        );
                      } else if (
                        e.key === "ArrowUp" &&
                        speakerSuggestions.length > 0
                      ) {
                        e.preventDefault();
                        setShowSpeakerSuggestions(true);
                        setSelectedSpeakerSuggestionIndex((prev) =>
                          prev > 0 ? prev - 1 : 0
                        );
                      } else if (e.key === "Escape") {
                        setShowSpeakerSuggestions(false);
                        setSelectedSpeakerSuggestionIndex(-1);
                      }
                    }}
                    onFocus={() => {
                      if (speakersEmailInput.trim().length > 0) {
                        setShowSpeakerSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSpeakerSuggestions(false), 200);
                    }}
                    placeholder="Add speaker email and press Enter"
                    className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {/* Speaker User Status Indicator */}
                  {speakersEmailInput.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingSpeakerUser ? (
                        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : speakerUserExists === true ? (
                        <div className="text-green-500 text-sm">✓</div>
                      ) : speakerUserExists === false ? (
                        <Mail className="w-5 h-5 text-orange-500" />
                      ) : null}
                    </div>
                  )}
                  {/* Speaker Suggestions Dropdown */}
                  {showSpeakerSuggestions && speakerSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {speakerSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            handleAddSpeaker(suggestion, null);
                          }}
                          className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                            selectedSpeakerSuggestionIndex === index
                              ? "bg-indigo-100"
                              : ""
                          }`}
                        >
                          <div className="text-sm text-gray-700">
                            {suggestion}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Speaker User Exists/Invite Options */}
                  {!showSpeakerSuggestions &&
                    speakersEmailInput.trim() &&
                    speakerUserExists !== null &&
                    !checkingSpeakerUser && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        {speakerUserExists ? (
                          <div
                            onClick={() =>
                              handleAddSpeaker(speakersEmailInput.trim(), false)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-green-500">✓</div>
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {speakersEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User exists - Add as Speaker
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() =>
                              handleAddSpeaker(speakersEmailInput.trim(), true)
                            }
                            className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <Mail className="w-5 h-5 text-orange-500" />
                              <div>
                                <div className="text-sm font-medium text-gray-700">
                                  {speakersEmailInput.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  User not found - Send Email Invite
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
                <button
                  onClick={() => {
                    if (speakersEmailInput.trim()) {
                      const email = speakersEmailInput.trim();
                      if (speakerUserExists === false) {
                        handleAddSpeaker(email, true);
                      } else {
                        handleAddSpeaker(email, false);
                      }
                    }
                  }}
                  disabled={checkingSpeakerUser}
                  className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingSpeakerUser ? "..." : "Add"}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {speakersList.map((email, index) => {
                const isInvite = speakerInviteEmails.includes(email);
                return (
                  <span
                    key={index}
                    className={`flex items-center px-3 py-1 rounded-full text-sm ${
                      isInvite
                        ? "bg-orange-600 text-white"
                        : "bg-gray-700 text-white"
                    }`}
                  >
                    {isInvite && <Mail className="w-3 h-3 mr-1" />}
                    {email}
                    <button
                      onClick={() => {
                        setSpeakersList((prev) =>
                          prev.filter((item) => item !== email)
                        );
                        setSpeakerInviteEmails((prev) =>
                          prev.filter((item) => item !== email)
                        );
                      }}
                      className="ml-2 text-gray-400 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Room Settings */}
          <div className="mb-8">
            <h3 className="text-white text-lg font-semibold mb-6">
              ROOM SETTINGS
            </h3>

            <div className="space-y-6">
              {/* Allow Chat */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Allow Chat</h4>
                  <p className="text-gray-400 text-sm">
                    (Enable or disable in-meeting chat for participants.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleInputChange("allowChat", true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.allowChat
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => handleInputChange("allowChat", false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.allowChat
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Allow Screen Sharing - Hidden for Webinar */}
              {formData.roomType !== "Webinar" && (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">
                      Allow Screen Sharing
                    </h4>
                    <p className="text-gray-400 text-sm">
                      (Let participants share their screen during the meeting.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() =>
                        handleInputChange("allowScreenSharing", true)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.allowScreenSharing === true
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Everyone
                    </button>
                    <button
                      onClick={() =>
                        handleInputChange("allowScreenSharing", false)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.allowScreenSharing === false
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Host only
                    </button>
                  </div>
                </div>
              )}

              {/* Mute participants on entry - Hidden for Webinar */}
              {formData.roomType !== "Webinar" && (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">
                      Mute participants on entry
                    </h4>
                    <p className="text-gray-400 text-sm">
                      (Hosts can unmute participants later.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() =>
                        handleInputChange("muteParticipantsOnEntry", true)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.muteParticipantsOnEntry
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() =>
                        handleInputChange("muteParticipantsOnEntry", false)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !formData.muteParticipantsOnEntry
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>
              )}

              {/* Allow Reactions */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Allow Reactions</h4>
                  <p className="text-gray-400 text-sm">
                    (Participants can use emoji reactions during the meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleInputChange("allowReactions", true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.allowReactions
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => handleInputChange("allowReactions", false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.allowReactions
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Allow Raise Hand */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Allow Raise Hand</h4>
                  <p className="text-gray-400 text-sm">
                    (Participants can raise their hand to request attention from
                    the host.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleInputChange("allowRaiseHand", true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.allowRaiseHand
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => handleInputChange("allowRaiseHand", false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.allowRaiseHand
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Guest can Unmute */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Guest can Unmute</h4>
                  <p className="text-gray-400 text-sm">
                    (Allow guests to unmute themselves during the meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleInputChange("guestCanUnmute", true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.guestCanUnmute
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => handleInputChange("guestCanUnmute", false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.guestCanUnmute
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Require Password */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Require Password</h4>
                  <p className="text-gray-400 text-sm">
                    (Participants will need to enter a password to join the
                    meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleInputChange("requirePassword", true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.requirePassword
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => handleInputChange("requirePassword", false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.requirePassword
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Password Input */}
              {formData.requirePassword && (
                <div className="pl-8">
                  <label className="block text-white text-sm font-medium mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    {[0, 1, 2, 3].map((index) => (
                      <input
                        key={index}
                        ref={(el) => (passwordInputRefs.current[index] = el)}
                        type="tel"
                        value={formData.password[index] || ""}
                        onChange={(e) => handlePasswordDigitChange(index, e.target.value)}
                        onKeyDown={(e) => handlePasswordKeyDown(index, e)}
                        onPaste={handlePasswordPaste}
                        maxLength={1}
                        required
                        className={`w-14 h-14 text-center text-xl font-semibold bg-white border-2 rounded-lg text-black focus:outline-none focus:ring-2 transition-colors ${
                          formData.password.length > 0 && formData.password.length !== 4
                            ? "border-red-500 focus:ring-red-500"
                            : formData.password.length === 4
                            ? "border-green-500 focus:ring-green-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs">
                    {formData.password.length > 0 && formData.password.length !== 4 ? (
                      <span className="text-red-400">
                        Password must be exactly 4 digits
                      </span>
                    ) : (
                      "4 digits, numbers only"
                    )}
                  </p>
                </div>
              )}

              {/* Waiting Room */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">Waiting Room</h4>
                  <p className="text-gray-400 text-sm">
                    (Participants will be placed in a waiting room before
                    joining the meeting.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() =>
                      handleInputChange("hostApprovalRequired", true)
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.hostApprovalRequired
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    On
                  </button>
                  <button
                    onClick={() =>
                      handleInputChange("hostApprovalRequired", false)
                    }
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !formData.hostApprovalRequired
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Off
                  </button>
                </div>
              </div>

              {/* Exit URL */}
              <div className="border-t border-gray-600 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">Exit URL</h4>
                    <p className="text-gray-400 text-sm">
                      (Redirect participants to a specific URL when they leave
                      the meeting.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleInputChange("exitUrlEnabled", true)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.exitUrlEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => {
                        handleInputChange("exitUrlEnabled", false);
                        handleInputChange("exitUrl", "");
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !formData.exitUrlEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                {formData.exitUrlEnabled && (
                  <div className="pl-4">
                    <label className="block text-white text-sm font-medium mb-2">
                      Exit URL
                    </label>
                    <input
                      type="url"
                      value={formData.exitUrl}
                      onChange={(e) =>
                        handleInputChange("exitUrl", e.target.value)
                      }
                      placeholder="https://example.com"
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                        formData.exitUrl.trim() &&
                        !isValidUrl(formData.exitUrl.trim())
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-600 focus:ring-[#6A1FBF]"
                      }`}
                    />
                    {formData.exitUrl.trim() &&
                      !isValidUrl(formData.exitUrl.trim()) && (
                        <p className="text-red-400 text-xs mt-1">
                          Please enter a valid URL (e.g., https://example.com)
                        </p>
                      )}
                    {formData.exitUrl.trim() &&
                      isValidUrl(formData.exitUrl.trim()) && (
                        <p className="text-green-400 text-xs mt-1">
                          Valid URL ✓
                        </p>
                      )}
                  </div>
                )}
              </div>

              {/* Custom Button */}
              <div className="border-t border-gray-600 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">Custom Button</h4>
                    <p className="text-gray-400 text-sm">
                      (Add a custom button with a link in the meeting room.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() =>
                        handleInputChange("customButtonEnabled", true)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.customButtonEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => {
                        handleInputChange("customButtonEnabled", false);
                        handleInputChange("customButtonText", "");
                        handleInputChange("customButtonUrl", "");
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !formData.customButtonEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                {formData.customButtonEnabled && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Button Name
                      </label>
                      <input
                        type="text"
                        value={formData.customButtonText}
                        onChange={(e) =>
                          handleInputChange("customButtonText", e.target.value)
                        }
                        placeholder="e.g., Visit Our Website"
                        maxLength={50}
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          formData.customButtonText.trim() &&
                          formData.customButtonText.trim().length < 3
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.customButtonText.trim() &&
                        formData.customButtonText.trim().length < 3 ? (
                          <span className="text-red-400">
                            Minimum 3 characters required
                          </span>
                        ) : (
                          `${formData.customButtonText.length}/50 characters`
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Button URL
                      </label>
                      <input
                        type="url"
                        value={formData.customButtonUrl}
                        onChange={(e) =>
                          handleInputChange("customButtonUrl", e.target.value)
                        }
                        placeholder="https://example.com"
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          formData.customButtonUrl.trim() &&
                          !isValidUrl(formData.customButtonUrl.trim())
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                      {formData.customButtonUrl.trim() &&
                        !isValidUrl(formData.customButtonUrl.trim()) && (
                          <p className="text-red-400 text-xs mt-1">
                            Please enter a valid URL (e.g., https://example.com)
                          </p>
                        )}
                      {formData.customButtonUrl.trim() &&
                        isValidUrl(formData.customButtonUrl.trim()) && (
                          <p className="text-green-400 text-xs mt-1">
                            Valid URL ✓
                          </p>
                        )}
                    </div>
                  </div>
                )}
              </div>

              {/* Waiting Room Text */}
              <div className="border-t border-gray-600 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">
                      Waiting Room Text
                    </h4>
                    <p className="text-gray-400 text-sm">
                      (Customize the messages shown to participants in the
                      waiting room.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() =>
                        handleInputChange("waitingRoomTextEnabled", true)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.waitingRoomTextEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => {
                        handleInputChange("waitingRoomTextEnabled", false);
                        handleInputChange("waitingRoomTitle", "");
                        handleInputChange("waitingRoomMessage", "");
                        handleInputChange("waitingRoomSubText", "");
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !formData.waitingRoomTextEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                {formData.waitingRoomTextEnabled && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.waitingRoomTitle}
                        onChange={(e) =>
                          handleInputChange("waitingRoomTitle", e.target.value)
                        }
                        placeholder="e.g., Waiting for Host to Approve"
                        maxLength={100}
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          formData.waitingRoomTitle.trim() &&
                          formData.waitingRoomTitle.trim().length < 3
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.waitingRoomTitle.trim() &&
                        formData.waitingRoomTitle.trim().length < 3 ? (
                          <span className="text-red-400">
                            Minimum 3 characters required
                          </span>
                        ) : (
                          `${formData.waitingRoomTitle.length}/100 characters`
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.waitingRoomMessage}
                        onChange={(e) =>
                          handleInputChange(
                            "waitingRoomMessage",
                            e.target.value
                          )
                        }
                        placeholder="e.g., Please wait while the host reviews your request to join"
                        maxLength={200}
                        rows={2}
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 resize-none ${
                          formData.waitingRoomMessage.trim() &&
                          formData.waitingRoomMessage.trim().length < 3
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.waitingRoomMessage.trim() &&
                        formData.waitingRoomMessage.trim().length < 3 ? (
                          <span className="text-red-400">
                            Minimum 3 characters required
                          </span>
                        ) : (
                          `${formData.waitingRoomMessage.length}/200 characters`
                        )}
                      </p>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Sub Text <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.waitingRoomSubText}
                        onChange={(e) =>
                          handleInputChange(
                            "waitingRoomSubText",
                            e.target.value
                          )
                        }
                        placeholder="e.g., You will be automatically admitted once approved"
                        maxLength={150}
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          formData.waitingRoomSubText.trim() &&
                          formData.waitingRoomSubText.trim().length < 3
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-600 focus:ring-[#6A1FBF]"
                        }`}
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.waitingRoomSubText.trim() &&
                        formData.waitingRoomSubText.trim().length < 3 ? (
                          <span className="text-red-400">
                            Minimum 3 characters required
                          </span>
                        ) : (
                          `${formData.waitingRoomSubText.length}/150 characters`
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Background */}
              <div className="border-t border-gray-600 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">
                      Custom Background
                    </h4>
                    <p className="text-gray-400 text-sm">
                      (Add a custom background to your meeting room.)
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() =>
                        handleInputChange("customBackgroundEnabled", true)
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.customBackgroundEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => {
                        handleInputChange("customBackgroundEnabled", false);
                        handleInputChange("backgroundImage", null);
                        handleInputChange("backgroundImagePreview", null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !formData.customBackgroundEnabled
                          ? "bg-[#6A1FBF] text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                {formData.customBackgroundEnabled && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() =>
                          handleInputChange("backgroundType", "solid")
                        }
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.backgroundType === "solid"
                            ? "bg-[#6A1FBF] text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                      >
                        Solid Color
                      </button>
                      <button
                        onClick={() =>
                          handleInputChange("backgroundType", "gradient")
                        }
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.backgroundType === "gradient"
                            ? "bg-[#6A1FBF] text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                      >
                        Gradient
                      </button>
                      <button
                        onClick={() =>
                          handleInputChange("backgroundType", "image")
                        }
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.backgroundType === "image"
                            ? "bg-[#6A1FBF] text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                      >
                        Image
                      </button>
                    </div>

                    {/* Solid Color Picker */}
                    {formData.backgroundType === "solid" && (
                      <div className="pl-4">
                        <label className="block text-white text-sm font-medium mb-2">
                          Background Color
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="color"
                            value={formData.backgroundColor}
                            onChange={(e) =>
                              handleInputChange(
                                "backgroundColor",
                                e.target.value
                              )
                            }
                            className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.backgroundColor}
                            onChange={(e) =>
                              handleInputChange(
                                "backgroundColor",
                                e.target.value
                              )
                            }
                            placeholder="#1a1a2e"
                            className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                          />
                          <div
                            className="w-20 h-10 rounded border border-gray-600"
                            style={{
                              backgroundColor: formData.backgroundColor,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Gradient Color Picker */}
                    {formData.backgroundType === "gradient" && (
                      <div className="pl-4 space-y-4">
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            First Color
                          </label>
                          <div className="flex items-center space-x-4">
                            <input
                              type="color"
                              value={formData.gradientColor1}
                              onChange={(e) =>
                                handleInputChange(
                                  "gradientColor1",
                                  e.target.value
                                )
                              }
                              className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={formData.gradientColor1}
                              onChange={(e) =>
                                handleInputChange(
                                  "gradientColor1",
                                  e.target.value
                                )
                              }
                              placeholder="#1a1a2e"
                              className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Second Color
                          </label>
                          <div className="flex items-center space-x-4">
                            <input
                              type="color"
                              value={formData.gradientColor2}
                              onChange={(e) =>
                                handleInputChange(
                                  "gradientColor2",
                                  e.target.value
                                )
                              }
                              className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={formData.gradientColor2}
                              onChange={(e) =>
                                handleInputChange(
                                  "gradientColor2",
                                  e.target.value
                                )
                              }
                              placeholder="#615fff"
                              className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Gradient Direction
                          </label>
                          <select
                            value={formData.gradientDirection}
                            onChange={(e) =>
                              handleInputChange(
                                "gradientDirection",
                                e.target.value
                              )
                            }
                            className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-[#6A1FBF]"
                          >
                            <option value="to right">Left to Right</option>
                            <option value="to left">Right to Left</option>
                            <option value="to bottom">Top to Bottom</option>
                            <option value="to top">Bottom to Top</option>
                            <option value="to bottom right">Diagonal ↘</option>
                            <option value="to bottom left">Diagonal ↙</option>
                            <option value="to top right">Diagonal ↗</option>
                            <option value="to top left">Diagonal ↖</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Preview
                          </label>
                          <div
                            className="w-full h-20 rounded border border-gray-600"
                            style={{
                              background: `linear-gradient(${formData.gradientDirection}, ${formData.gradientColor1}, ${formData.gradientColor2})`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Image Upload */}
                    {formData.backgroundType === "image" && (
                      <div className="pl-4 space-y-4">
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Background Image
                          </label>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const maxSize = 5 * 1024 * 1024;
                                if (file.size > maxSize) {
                                  toast.error(
                                    "Image size should not exceed 5MB"
                                  );
                                  e.target.value = "";
                                  return;
                                }
                                handleInputChange("backgroundImage", file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  handleInputChange(
                                    "backgroundImagePreview",
                                    reader.result
                                  );
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="block w-full text-sm text-gray-400
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-lg file:border-0
                            file:text-sm file:font-semibold
                            file:bg-[#6A1FBF] file:text-white
                            hover:file:bg-[#7B2FCF]
                            file:cursor-pointer cursor-pointer"
                          />
                          <p className="text-gray-400 text-xs mt-1">
                            Maximum file size: 5MB
                          </p>
                          {formData.backgroundImage && (
                            <p className="text-green-400 text-xs mt-1">
                              Selected: {formData.backgroundImage.name} (
                              {(formData.backgroundImage.size / 1024).toFixed(
                                2
                              )}{" "}
                              KB)
                            </p>
                          )}
                        </div>

                        {formData.backgroundImagePreview && (
                          <div>
                            <label className="block text-white text-sm font-medium mb-2">
                              Preview
                            </label>
                            <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-600 bg-gray-800">
                              <img
                                src={formData.backgroundImagePreview}
                                alt="Background preview"
                                className="w-full h-full object-contain"
                              />
                              <button
                                onClick={() => {
                                  handleInputChange("backgroundImage", null);
                                  handleInputChange(
                                    "backgroundImagePreview",
                                    null
                                  );
                                  if (fileInputRef.current) {
                                    fileInputRef.current.value = "";
                                  }
                                }}
                                className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                              >
                                <X size={16} />
                                <span className="text-sm">Remove</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-8">
            <button
              onClick={() => navigate("/dashboard/rooms")}
              className="bg-gray-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="bg-[#6A1FBF] text-white font-bold py-3 px-8 rounded-lg hover:bg-[#7B2FCF] transition-colors"
            >
              Create Room
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewRoom;
